import { captureAttribution, initGtm, router, themeConfig } from "@shared";
import { StoreContext, store } from "@stores";
import { App as AntdApp, ConfigProvider, Spin } from "antd";
import { AppMessageBridge } from "@messages";
import enUS from "antd/locale/en_US";
import srRS from "antd/locale/sr_RS";
import dayjs from "dayjs";
import "dayjs/locale/en";
import "dayjs/locale/sr";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import "./i18n";
import "./toast.css";
import { observer } from "mobx-react-lite";
import ReactDOM from "react-dom/client";
import { useTranslation } from "react-i18next";
import { RouterProvider } from "react-router-dom";

dayjs.extend(utc);
dayjs.extend(timezone);

// Keep the campaign (UTM) parameters of the landing page, then start GTM (consent denied until the banner is answered)
captureAttribution();
initGtm();

// Fetch localization data at app startup (before auth)
store.authStore.initAbpData();

const App = observer(() => {
  const { i18n: i18nInstance } = useTranslation();
  const currentLang = i18nInstance.language;
  const locale = currentLang === "en" ? enUS : srRS;

  dayjs.locale(currentLang === "en" ? "en" : "sr");

  if (!store.authStore.localizationReady) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <ConfigProvider theme={themeConfig} locale={locale}>
      <AntdApp component={false}>
        <AppMessageBridge />
        <RouterProvider router={router} />
      </AntdApp>
    </ConfigProvider>
  );
});

const root = ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement,
);

root.render(
  <StoreContext.Provider value={store}>
    <App />
  </StoreContext.Provider>,
);

// Core Web Vitals reporting
import { onCLS, onINP, onLCP } from "web-vitals";

const reportWebVital = (metric: { name: string; value: number; delta: number; id: string }) => {
  if (import.meta.env.DEV) {
    console.log(`[Web Vital] ${metric.name}:`, Math.round(metric.value));
  }
};

onCLS(reportWebVital);
onINP(reportWebVital);
onLCP(reportWebVital);
